import { DAY_DATE_FORMAT, ENGLISH, SHORT_HOUR_DATE_FORMAT } from '@/helpers/constants';
import { getFullFormattedDate, changeTimeZoneFormat, sortJsonKeysForComparison } from '@/helpers/utils';
import campaignServices from '@/services/campaignServices';
import { mapVolumesNOKOK, mapDatesVehicleGroups } from './VehicleCleanDirt/VehicleCleanDirt-utils';
import { INPUTS, COLUMNS } from './volumeDetails-constants';

export const mapVolumes = (volumes, plantSelected, label, headerLanguage, value) => volumes?.map((currentVolume) => (currentVolume?.plant === plantSelected?.wkzNumber
  ? {
    ...currentVolume,
    limitationVolumeManualEntry: {
      ...currentVolume.limitationVolumeManualEntry,
      [label]: {
        ...currentVolume.limitationVolumeManualEntry[label],
        [headerLanguage]: value,
      },
    },
  }
  : currentVolume));

export const getOriginValuesNewState = (originalValues, plantSelected) => ({
  ...originalValues,
  hasChange: true,
  changesIn: {
    ...originalValues?.changesIn,
    volumes: {
      ...originalValues?.changesIn?.volumes,
      [plantSelected?.wkzNumber]: true,
    },
  },
});

export const mapAggregateList = (modelSeries = [], lang) => {
  let allRows = [];
  modelSeries.forEach((currentModelSerie) => {
    const mapedRows = currentModelSerie.vehicles.map((currentRow) => {
      const code = {
        aggregateCode: `${currentRow.codeCondition} - ${currentModelSerie.modelSeries} - ${currentModelSerie.typeVersion} - ${currentRow.modelSeriesNumber}`,
        codeCondition: currentRow.codeCondition,
        modelSeries: currentModelSerie.modelSeries,
        typeVersion: currentModelSerie.typeVersion,
      };

      return {
        Serie: currentModelSerie.modelSeries,
        TypeVersion: currentModelSerie.typeVersion,
        Aggregat: currentRow.modelSeriesNumber,
        CodeConditions: currentRow.codeCondition,
        Designation: currentRow.designationModelSeries[lang],
        TotallyCode: JSON.stringify(code),
      };
    });
    allRows = allRows.concat(mapedRows);
  });

  return allRows;
};

export const mapCountryVariantsList = (resultList, lang) => resultList?.map((current) => ({
  HiddenId: current.id,
  CountryCode: current.name,
  Country: current.description?.[lang],
}));

export const mapPaintCodesList = (resultList, lang) => resultList?.map((current) => ({
  HiddenId: current.id,
  PaintCode: current.name,
  Description: current.description?.[lang],
}));

export const mapVehiclesModelDesignation = (resultList, lang) => resultList?.map((current) => {
  const code = {
    modelDesignation: current.baumusterVariant,
    modelSeries: current.modelSeries,
    typeVersion: current.typeVersion,
  };

  return {
    codeCondition: current.codeCondition,
    typeVersion: current.typeVersion,
    steering: current.steering,
    driveType: current.driveType,
    bmaaDesignation: current.bmaaDesignation[lang],
    baumusterVariant: current.baumusterVariant,
    salesDesignation: current.salesDesignation[lang],
    modelSeries: current.modelSeries,
    TotallyCode: JSON.stringify(code),
  };
});

export const handleInputsChanges = (label, value, volumes, plantSelected, setVolumesOriginhadChanged, originalValues) => {
  setVolumesOriginhadChanged({
    originalValues: getOriginValuesNewState(originalValues, plantSelected),
    volumes: mapVolumesNOKOK(label, plantSelected, volumes, value),
  });
};

const normalizeSelection = (selection = []) => selection.map((value) => String(sortJsonKeysForComparison(value))).sort();

export const handleSelectionChanges = (label, selectedOptions, volumes, plantSelected, setVolumesOriginhadChanged, originalValues, list, setCopy) => {
  const currentSelection = getVolumeSelected(label, volumes, plantSelected) || [];
  const normalizedCurrent = normalizeSelection(currentSelection);
  const normalizedNext = normalizeSelection(selectedOptions || []);
  const hasRealChange = normalizedCurrent.length !== normalizedNext.length
    || normalizedCurrent.some((value, index) => value !== normalizedNext[index]);

  if (!hasRealChange) return;

  handleInputsChanges(label, selectedOptions, volumes, plantSelected, setVolumesOriginhadChanged, originalValues);

  if (['aggregateSelecteds', 'vehicleModelDesignationSelecteds'].includes(label)) {
    const listSelec = selectedOptions.map((sel) => list.find((li) => li[label === 'aggregateSelecteds' ? 'TotallyCode' : 'baumusterVariant'] === sel));
    setCopy(listSelec);
  }
};

export const getVolumeSelected = (label, volumes, plantSelected) => {
  if (!volumes || volumes.length === 0) return [];

  const volumeFound = volumes?.find((currentVolume) => currentVolume?.plant === plantSelected?.wkzNumber);

  return volumeFound?.limitationVolumeManualEntry?.[label];
};

const getChipsLabels = (label, volumes, plantSelected) => {
  let optionsLabels = getVolumeSelected(label, volumes, plantSelected);

  if (label === 'vehicleModelDesignationSelecteds') {
    optionsLabels = optionsLabels?.map((option) => JSON.parse(option)?.modelDesignation);
  }

  if (label === 'aggregateSelecteds') {
    optionsLabels = optionsLabels?.map((option) => JSON.parse(option)?.aggregateCode);
  }

  return optionsLabels;
};

const mapDate = ({ calendarDate, timeDate }, onlyTime) => (onlyTime ? `${calendarDate} ${timeDate}:00 +0000` : calendarDate);

const getDatesVehicleCleanDirt = (section, timeZone, onlyTime) => ({
  ...section,
  dates: section?.dates.map((date) => (
    !date.calendarDate
      ? date
      : {
        textDate: date.textDate,
        calendarDate: getFullFormattedDate(mapDate(date, onlyTime), timeZone, DAY_DATE_FORMAT),
        timeDate: getFullFormattedDate(mapDate(date, onlyTime), timeZone, SHORT_HOUR_DATE_FORMAT),
      })),
});

export const getSelectedValues = (label, volumes, plantSelected) => getChipsLabels(label, volumes, plantSelected)?.map((currentOption) => ({ key: currentOption, label: currentOption }));

export const removeSelected = (label, chipToDelete, volumes, plantSelected, setVolumesOriginhadChanged, originalValues) => {
  const optionsLabels = getVolumeSelected(label, volumes, plantSelected);
  const newSelection = optionsLabels.filter((currentLabel) => {
    if (label === 'vehicleModelDesignationSelecteds') {
      return JSON.parse(currentLabel).modelDesignation !== chipToDelete.key;
    }

    if (label === 'aggregateSelecteds') {
      return JSON.parse(currentLabel).aggregateCode !== chipToDelete.key;
    }

    return currentLabel !== chipToDelete.key;
  });
  handleInputsChanges(label, newSelection, volumes, plantSelected, setVolumesOriginhadChanged, originalValues);
};

export const getTabInputs = (currentTab) => INPUTS.find(({ tab }) => tab === currentTab)?.inputs;

export const getColumnsData = (currentTab, t) => ({
  columns: COLUMNS[currentTab].map(({ colName, colTitle, ...columnConfig }) => ({
    name: colName,
    title: t(colTitle),
    ...columnConfig,
  })),
});

export const copyAggregate = (optionsSelected, engines, setCopyAggreagate) => {
  const listSelec = [];
  optionsSelected
    .map((op) => engines
      .forEach((vh, value) => {
        const vhFound = vh.vehicles
          .find((re) => `${re.codeCondition} - ${engines[value].modelSeries} - ${engines[value].typeVersion} - ${re.modelSeriesNumber}` === op);

        if (vhFound) {
          vhFound.Serie = engines[value].modelSeries;
          vhFound.TypeVersion = engines[value].typeVersion;
          vhFound.TotallyCode = `${vhFound.codeCondition} - ${vhFound.Serie} - ${vhFound.TypeVersion} - ${vhFound.modelSeriesNumber}`;
          listSelec.push(vhFound);
        }
      }));
  setCopyAggreagate(listSelec);
};

export const copyModelDesignation = (optionsSelected, models, setCopyModelDesignation) => {
  const listSelec = optionsSelected.map((op) => models.find((re) => re.baumusterVariant === op));
  setCopyModelDesignation(listSelec);
};

export const mapAggregateModelSelections = (volumes, timeZone, onlyTime) => volumes?.map((volume) => ({
  ...volume,
  limitationVolumeManualEntry: {
    ...volume.limitationVolumeManualEntry,
    ...(!onlyTime && {
      aggregateSelecteds: volume.limitationVolumeManualEntry.aggregateSelecteds?.map(JSON.stringify),
      vehicleModelDesignationSelecteds: volume.limitationVolumeManualEntry.vehicleModelDesignationSelecteds.map(JSON.stringify),
    }),
    vehicleGroups: volume.limitationVolumeManualEntry.vehicleGroups?.filter(({ selections }) => selections.length > 0)
      ?.map((group) => ({
        ...group,
        cleanPoint: getDatesVehicleCleanDirt(group.cleanPoint, timeZone, onlyTime),
        dirtPoint: getDatesVehicleCleanDirt(group.dirtPoint, timeZone, onlyTime),
      })),
  },
}));

export const mapUpdateVolumeData = (volume, wkzNumber, timeZone, fileOption) => {
  const timezone = changeTimeZoneFormat(timeZone);
  const limitation = volume?.limitationVolumeManualEntry;
  const vehicleGroups = limitation?.vehicleGroups.filter(({ selections }) => selections.length > 0);
  const data = {
    ...volume,
    fileOptionSelected: fileOption || volume?.fileOptionSelected,
    limitationVolumeManualEntry: {
      ...limitation,
      vehicleModelDesignationSelecteds: limitation?.vehicleModelDesignationSelecteds?.map((model) => JSON.parse(model)),
      aggregateSelecteds: limitation?.aggregateSelecteds?.map((model) => JSON.parse(model)),
      vehicleGroups: mapDatesVehicleGroups(vehicleGroups, timezone),
    },
  };

  return data;
};

export const getPlantSelectVolume = (volumes, wkz) => volumes?.find(({ plant }) => plant === wkz);

export const updateCampaignActiveLanguages = (originalValues, plantsToUse, campaignInfo, setInfo) => {
  const oldPlants = (originalValues?.affectedPlants || originalValues?.values?.affectedPlants)?.map((plant) => plant.wkzNumber) || [];
  const plantsToUseNumbers = plantsToUse.map(({ wkzNumber }) => wkzNumber);

  if (oldPlants.length === plantsToUseNumbers.length && oldPlants.every((plant) => plantsToUseNumbers.includes(plant))) return;
  if (plantsToUse.length === 0) {
    const newInfoState = {
      ...campaignInfo,
      activeLanguages: campaignInfo?.activeLanguages?.filter(({ languageCode }) => languageCode === campaignInfo?.defaultLanguage),
    };

    setInfo(newInfoState, false);
  } else {
    const wkzs = plantsToUse?.map(({ wkzNumber }) => wkzNumber)?.join();
    campaignServices.getPlantsLanguages(wkzs).then((res) => {
      const languageTranslation = campaignInfo.defaultLanguage === 'EN' ? 'DE' : 'EN';
      const showTranslationTab = res.languageCodes.includes(languageTranslation);
      const activeLanguages = showTranslationTab ? ['EN', 'DE'] : [campaignInfo.defaultLanguage];
      const newInfoState = {
        ...campaignInfo,
        activeLanguages: activeLanguages.map((languageCode) => ({ languageCode, lastModification: new Date(), new: true })),
      };

      setInfo(newInfoState, false);
    });
  }
};
